/**
 * React bindings, shared by the landing site and the Electron renderers.
 *
 * Two separate contexts. The client is one per app; the view nests, one per
 * page, panel, modal or popover. Holding the view in context is what lets a
 * button report `view_name` without every call site threading it down by hand.
 *
 * "View" follows the sidepanel's view registry, whose ids drop straight in as
 * `view_name`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from 'react'

import {
  ANALYTICS_EVENTS,
  type AnalyticsClient,
  type AnalyticsProperties,
  type InteractionProperties,
  noopAnalytics,
  type ViewKind,
} from './core'
import { createFlagReader, createStickyLookup, fallbackClientFlags, type ClientFlags, type FlagReader } from './flags'

const AnalyticsContext = createContext<AnalyticsClient>(noopAnalytics)
const ViewContext = createContext<string | undefined>(undefined)

export interface AnalyticsProviderProps {
  client: AnalyticsClient
  children: ReactNode
}

export function AnalyticsProvider({ client, children }: AnalyticsProviderProps) {
  return <AnalyticsContext.Provider value={client}>{children}</AnalyticsContext.Provider>
}

/**
 * The analytics client for the current tree.
 *
 * Never throws when no provider is mounted. It returns the no-op client, so a
 * component renders in Storybook or a test without one.
 */
export function useAnalytics(): AnalyticsClient {
  return useContext(AnalyticsContext)
}

export function useViewName(): string | undefined {
  return useContext(ViewContext)
}

export interface AnalyticsViewProps {
  name: string
  kind?: ViewKind
  properties?: AnalyticsProperties
  /** Set false for a view that is mounted but not yet visible. */
  active?: boolean
  children: ReactNode
}

/**
 * Names a view for its subtree and emits shown/hidden events for it.
 *
 * This is the navigation signal: a route, a sidepanel panel, a settings tab, a
 * modal, a popover. Think pageview, extended to surfaces that aren't pages.
 *
 * Mount and unmount are the visibility boundary, so a modal rendered
 * conditionally reports correctly with no extra wiring. A view kept mounted
 * behind an `active` flag should pass that flag instead.
 */
export function AnalyticsView({ name, kind = 'page', properties, active = true, children }: AnalyticsViewProps) {
  const analytics = useAnalytics()
  const parentViewName = useViewName()
  // Read at fire time rather than captured in the effect, so the hidden event
  // reports the properties the view had when it closed. Snapshotting at show
  // time made a multi-step view always report step 1 on the way out, which is
  // precisely the number a drop-off funnel is built on. Written in an effect
  // because a ref mutation during render is discarded work under a transition.
  const propertiesRef = useRef(properties)
  useEffect(() => {
    propertiesRef.current = properties
  })

  useEffect(() => {
    if (!active) {
      return
    }

    const describe = () => ({
      view_name: name,
      view_kind: kind,
      parent_view_name: parentViewName,
      ...propertiesRef.current,
    })

    analytics.capture(ANALYTICS_EVENTS.UI_VIEW_SHOWN, describe())
    return () => analytics.capture(ANALYTICS_EVENTS.UI_VIEW_HIDDEN, describe())
  }, [analytics, name, kind, parentViewName, active])

  return <ViewContext.Provider value={name}>{children}</ViewContext.Provider>
}

/**
 * Returns a `trackInteraction` already bound to the current view.
 *
 * Instrumented button primitives are built on this. Call it directly for a
 * control that can't be one of them.
 */
export function useTrackInteraction(): (properties: InteractionProperties) => void {
  const analytics = useAnalytics()
  const viewName = useViewName()

  return useCallback(
    (properties: InteractionProperties) => {
      analytics.capture(ANALYTICS_EVENTS.UI_INTERACTION, { view_name: viewName, ...properties })
    },
    [analytics, viewName],
  )
}

const FlagsContext = createContext<ClientFlags>(fallbackClientFlags)

export function FlagsProvider({ flags, children }: { flags: ClientFlags; children: ReactNode }) {
  return <FlagsContext.Provider value={flags}>{children}</FlagsContext.Provider>
}

const subscribeNever = () => () => {}

export interface UseFlagsOptions {
  /**
   * Re-read on every refresh instead of keeping each value for the life of
   * the component. For kill switches that must take effect in an open view;
   * everything else keeps what the user is looking at from changing under them.
   */
  live?: boolean
}

/**
 * The flags for the current tree.
 *
 * Read them during render. A read records its exposure; posthog-js dedupes it
 * per session.
 *
 * Once flags are ready, each key keeps its first value for the life of the
 * component, so a focus or background refresh cannot flip a mounted view.
 * Before that, reads follow the live flags. Render a loading state on
 * `ready` where a flip at startup or sign-in would be visible.
 *
 * Fallbacks until hydration finishes, since the server rendered with
 * fallbacks and posthog-js may already hold flags from a previous visit.
 * posthog-js calls an `onChange` listener at once if flags loaded before it
 * subscribed, so a load between render and subscribe is not missed.
 */
export function useFlags({ live = false }: UseFlagsOptions = {}): FlagReader & { ready: boolean } {
  const flags = useContext(FlagsContext)
  const hydrated = useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  )
  const [, rerender] = useReducer((count: number) => count + 1, 0)
  useEffect(() => flags.onChange(rerender), [flags])

  const sticky = useMemo(() => createStickyLookup(flags.lookup), [flags])
  const reader = useMemo(() => createFlagReader(sticky.lookup), [sticky])

  if (!hydrated) {
    return { ...fallbackClientFlags, ready: false }
  }
  const ready = flags.isReady()
  if (live || !ready) {
    // A person who is not ready yet is about to get different values, so
    // nothing read now may stick.
    sticky.reset()
    return { ...flags, ready }
  }
  return { ...reader, ready }
}
